"use client";

type Props = Record<string, any> | undefined;
type CaptureOptions = Record<string, unknown> | undefined;

export type QueuedIdentity = { id: string; props?: Record<string, any>; ts: number };
export type QueuedCapture = {
  event: string;
  properties?: Record<string, any>;
  options?: Record<string, unknown>;
  ts: number;
};

export type QueuedRegistration = {
  register: Record<string, any>;
  registerOnce: Record<string, any>;
  ts: number;
};

export const IDENTITY_STORAGE_KEY = "ph_queued_identity";
export const REGISTRATION_STORAGE_KEY = "ph_queued_registration";
export const CAPTURE_STORAGE_KEY = "ph_queued_captures";
export const IDENTITY_TTL_MS = 30 * 60 * 1000;
export const REGISTRATION_TTL_MS = 30 * 60 * 1000;
export const CAPTURE_TTL_MS = 10 * 60 * 1000;
export const MAX_QUEUED_CAPTURES = 30;

function readQueuedCaptures(): QueuedCapture[] {
  try {
    if (typeof window === "undefined" || !window.localStorage) return [];
    const raw = window.localStorage.getItem(CAPTURE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      window.localStorage.removeItem(CAPTURE_STORAGE_KEY);
      return [];
    }

    const now = Date.now();
    const valid = parsed.filter(
      (item): item is QueuedCapture =>
        item &&
        typeof item === "object" &&
        typeof item.event === "string" &&
        typeof item.ts === "number" &&
        now - item.ts <= CAPTURE_TTL_MS,
    );

    if (valid.length !== parsed.length) {
      window.localStorage.setItem(CAPTURE_STORAGE_KEY, JSON.stringify(valid));
    }

    return valid;
  } catch {
    return [];
  }
}

function writeQueuedCaptures(captures: QueuedCapture[]): void {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(
      CAPTURE_STORAGE_KEY,
      JSON.stringify(captures.slice(-MAX_QUEUED_CAPTURES)),
    );
  } catch {
  }
}

function readQueuedRegistration(): QueuedRegistration | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(REGISTRATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as QueuedRegistration;
    if (!parsed || typeof parsed.ts !== "number" || (Date.now() - parsed.ts) > REGISTRATION_TTL_MS) {
      window.localStorage.removeItem(REGISTRATION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeQueuedRegistration(queued: QueuedRegistration): void {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(REGISTRATION_STORAGE_KEY, JSON.stringify(queued));
    }
  } catch {
  }
}

export function phQueueCapture(
  event: string,
  properties?: Props,
  options?: CaptureOptions,
): void {
  const queued = readQueuedCaptures();
  queued.push({
    event,
    properties: properties ?? undefined,
    options: options ?? undefined,
    ts: Date.now(),
  });
  writeQueuedCaptures(queued);
}

export function phQueueRegister(props: Record<string, any>): void {
  const existing = readQueuedRegistration();
  const queued: QueuedRegistration = {
    register: { ...(existing?.register || {}), ...props },
    registerOnce: existing?.registerOnce || {},
    ts: Date.now(),
  };
  writeQueuedRegistration(queued);
}

export function phQueueRegisterOnce(props: Record<string, any>): void {
  const existing = readQueuedRegistration();
  const queued: QueuedRegistration = {
    register: existing?.register || {},
    registerOnce: { ...props, ...(existing?.registerOnce || {}) },
    ts: Date.now(),
  };
  writeQueuedRegistration(queued);
}

export function phQueueIdentity(
  distinctId: string,
  properties?: Props,
): void {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      const payload: QueuedIdentity = { id: distinctId, props: properties || {}, ts: Date.now() };
      window.localStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(payload));
    }
  } catch {
  }
}

export function phClearQueuedAnalytics(): void {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.removeItem(IDENTITY_STORAGE_KEY);
      window.localStorage.removeItem(REGISTRATION_STORAGE_KEY);
      window.localStorage.removeItem(CAPTURE_STORAGE_KEY);
    }
  } catch {
  }
}

export function phConsumeQueuedIdentity(): { id: string; props?: Record<string, any> } | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(IDENTITY_STORAGE_KEY);
    if (!raw) return null;
    let parsed: QueuedIdentity | null = null;
    try {
      parsed = JSON.parse(raw) as QueuedIdentity;
    } catch {
      window.localStorage.removeItem(IDENTITY_STORAGE_KEY);
      return null;
    }
    if (!parsed || typeof parsed.id !== "string" || typeof parsed.ts !== "number" || (Date.now() - parsed.ts) > IDENTITY_TTL_MS) {
      window.localStorage.removeItem(IDENTITY_STORAGE_KEY);
      return null;
    }
    window.localStorage.removeItem(IDENTITY_STORAGE_KEY);
    return { id: parsed.id, props: parsed.props || {} };
  } catch {
    return null;
  }
}

export function phConsumeQueuedCaptures(): Array<{
  event: string;
  properties?: Record<string, any>;
  options?: Record<string, unknown>;
}> {
  try {
    if (typeof window === "undefined" || !window.localStorage) return [];
    const captures = readQueuedCaptures();
    window.localStorage.removeItem(CAPTURE_STORAGE_KEY);
    return captures.map((capture) => ({
      event: capture.event,
      properties: capture.properties,
      options: capture.options,
    }));
  } catch {
    return [];
  }
}

export function phConsumeQueuedRegistration(): { register: Record<string, any>; registerOnce: Record<string, any> } | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(REGISTRATION_STORAGE_KEY);
    if (!raw) return null;
    let parsed: QueuedRegistration | null = null;
    try {
      parsed = JSON.parse(raw) as QueuedRegistration;
    } catch {
      window.localStorage.removeItem(REGISTRATION_STORAGE_KEY);
      return null;
    }
    if (!parsed || typeof parsed.ts !== "number" || (Date.now() - parsed.ts) > REGISTRATION_TTL_MS) {
      window.localStorage.removeItem(REGISTRATION_STORAGE_KEY);
      return null;
    }
    window.localStorage.removeItem(REGISTRATION_STORAGE_KEY);
    return {
      register: parsed.register || {},
      registerOnce: parsed.registerOnce || {},
    };
  } catch {
    return null;
  }
}
